import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import EReader from '../../components/EReader/EReader';
import DictionaryPanel from '../../components/DictionaryPanel';
import './ReadSummary.css';

function countBookPages(allNotes, wordLimit = 250) {
    const limit = Math.max(50, Number(wordLimit) || 250);
    const totalWords = (allNotes || []).reduce((sum, note) => {
        return sum + String(note?.content || '').split(/\s+/).filter(Boolean).length;
    }, 0);
    return totalWords ? Math.ceil(totalWords / limit) : 0;
}

function ReadSummary() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [notes, setNotes] = useState([]);
    const [selectedNote, setSelectedNote] = useState(null);
    const [content, setContent] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editContent, setEditContent] = useState('');
    const [editTitle, setEditTitle] = useState('');
    const [editAudioLink, setEditAudioLink] = useState('');
    const [editTimingLink, setEditTimingLink] = useState('');
    const [showNoteForm, setShowNoteForm] = useState(false);
    const [newNote, setNewNote] = useState({ noteNumber: '', title: '', content: '', order: '', audioLink: '', timingLink: '' });
    const [bookTitle, setBookTitle] = useState('');
    const [bookId, setBookId] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [audioKey, setAudioKey] = useState(Date.now());
    const [dictionaryWord, setDictionaryWord] = useState('');
    const [dictionaryMobileOpen, setDictionaryMobileOpen] = useState(false);

    const convertToEnglishNumbers = (text) => {
        if (!text) return '';
        const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        const englishNumbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
        
        let result = text;
        for (let i = 0; i < persianNumbers.length; i++) {
            result = result.split(persianNumbers[i]).join(englishNumbers[i]);
        }
        return result;
    };

    const translateText = async (text) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/translate`, {
                params: { text: text, source: 'en', target: 'fa' }
            });
            return response.data.translatedText;
        } catch (error) {
            return "⚠️ ترجمه در دسترس نیست";
        }
    };

    useEffect(() => {
        const userData = localStorage.getItem('currentUser');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setCurrentUser(user);
                setIsAdmin(user.role === 'admin');
                
                if (user.role !== 'admin') {
                    checkUserSubscription(user._id);
                } else {
                    setHasSubscription(true);
                    setLoading(false);
                }
            } catch (e) {
                console.error("Error parsing user:", e);
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);

    const checkUserSubscription = async (userId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/check-subscription/${userId}`);
            setHasSubscription(response.data.hasActiveSubscription);
        } catch (error) {
            console.error("Error checking subscription:", error);
            setHasSubscription(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchBook = async () => {
            try {
                let response;
                if (slug.match(/^[0-9a-fA-F]{24}$/)) {
                    response = await axios.get(`${process.env.REACT_APP_API_URL}/summary-book/${slug}`);
                } else {
                    response = await axios.get(`${process.env.REACT_APP_API_URL}/summary-book/slug/${slug}`);
                }
                const book = response.data;
                setBookTitle(book.title || 'Summary Book');
                setBookId(book._id);
            } catch (error) {
                console.error("Error fetching book:", error);
                setBookTitle('Summary Book');
            }
        };
        if (slug) {
            fetchBook();
        }
    }, [slug]);

    const fetchNotes = async () => {
        if (!bookId) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/summary-notes/${bookId}`);
            console.log('📥 نوت‌های دریافت شده:', response.data.map(n => ({ 
                noteNumber: n.noteNumber, 
                audioLink: n.audioLink 
            })));
            setNotes(response.data);
            if (response.data.length > 0 && !selectedNote) {
                const firstNote = response.data[0];
                setSelectedNote(firstNote);
                setContent(firstNote.content || '');
                setEditContent(firstNote.content || '');
                setEditTitle(firstNote.title || '');
                setEditAudioLink(firstNote.audioLink || '');
                setEditTimingLink(firstNote.timingLink || '');
                setAudioKey(Date.now());
            }
        } catch (error) {
            console.error("Error fetching notes:", error);
        }
    };

    useEffect(() => {
        if (bookId) {
            fetchNotes();
        }
    }, [bookId]);

    const handleNoteClick = (note, index) => {
        if (!isAdmin && !hasSubscription && index > 0) {
            alert('برای دسترسی به این بخش، لطفاً اشتراک تهیه کنید.');
            window.location.href = '/subscription';
            return;
        }
        
        console.log('🎵 کلیک روی نوت:', note.noteNumber, 'لینک صوتی:', note.audioLink);
        
        setSelectedNote(note);
        setContent(note.content || '');
        setEditContent(note.content || '');
        setEditTitle(note.title || '');
        setEditAudioLink(note.audioLink || '');
        setEditTimingLink(note.timingLink || '');
        setEditMode(false);
        setSidebarOpen(false);
        setAudioKey(Date.now());
    };

    const handleEditClick = () => {
        if (!selectedNote) return;
        setEditMode(true);
        setEditTitle(selectedNote.title || '');
        setEditContent(selectedNote.content || '');
        setEditAudioLink(selectedNote.audioLink || '');
        setEditTimingLink(selectedNote.timingLink || '');
    };

    const handleSaveContent = async () => {
        if (!selectedNote) return;
        
        console.log('💾 ذخیره نوت:', selectedNote.noteNumber, 'لینک صوتی جدید:', editAudioLink);
        
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/summary-note/${selectedNote._id}`, {
                title: editTitle,
                content: editContent,
                audioLink: editAudioLink || null,
                timingLink: editTimingLink || null
            });
            
            console.log('✅ پاسخ سرور:', response.data);
            
            const updatedNote = { 
                ...selectedNote, 
                title: editTitle, 
                content: editContent,
                audioLink: editAudioLink || null,
                timingLink: editTimingLink || null
            };
            setSelectedNote(updatedNote);
            setContent(editContent);
            setAudioKey(Date.now());
            
            const updatedNotes = notes.map(n => 
                n._id === selectedNote._id ? updatedNote : n
            );
            setNotes(updatedNotes);
            
            setEditMode(false);
            alert('✅ نوت با موفقیت ذخیره شد!');
        } catch (error) {
            console.error("❌ خطا در ذخیره نوت:", error);
            alert('خطا در ذخیره نوت');
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!bookId) return;
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/summary-note`, {
                bookId: bookId,
                noteNumber: parseInt(newNote.noteNumber),
                title: newNote.title,
                content: newNote.content,
                order: parseInt(newNote.order) || parseInt(newNote.noteNumber),
                audioLink: newNote.audioLink || null,
                timingLink: newNote.timingLink || null
            });
            
            console.log('✅ نوت جدید اضافه شد:', response.data);
            
            alert('Note added successfully!');
            setShowNoteForm(false);
            setNewNote({ noteNumber: '', title: '', content: '', order: '', audioLink: '', timingLink: '' });
            await fetchNotes();
        } catch (error) {
            console.error("Error adding note:", error);
            alert('Failed to add note');
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (window.confirm('Are you sure you want to delete this note?')) {
            try {
                await axios.delete(`${process.env.REACT_APP_API_URL}/summary-note/${noteId}`);
                alert('Note deleted successfully!');
                if (selectedNote?._id === noteId) setSelectedNote(null);
                await fetchNotes();
            } catch (error) {
                console.error("Error deleting note:", error);
                alert('Failed to delete note');
            }
        }
    };

    const currentNoteIndex = selectedNote ? notes.findIndex(n => n._id === selectedNote._id) : -1;
    const canAutoAdvanceNote = isAdmin || hasSubscription;
    const hasNextNote = canAutoAdvanceNote && currentNoteIndex >= 0 && currentNoteIndex < notes.length - 1;
    const goToNextNote = () => {
        if (!hasNextNote) return;
        handleNoteClick(notes[currentNoteIndex + 1], currentNoteIndex + 1);
    };

    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        const userData = localStorage.getItem('currentUser');
        if (userData) {
            try {
                const user = JSON.parse(userData);
                setCurrentUserId(user._id);
            } catch(e) {}
        }
    }, []);
    
    const handleSaveWord = async (word, meaning, context) => {
        if (!currentUserId) {
            alert('لطفاً ابتدا وارد شوید');
            window.location.href = '/login';
            return;
        }
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/save-word`, {
                userId: currentUserId,
                word: word,
                meaning: meaning,
                context: context,
                bookId: bookId,
                bookTitle: bookTitle
            });
            
            if (response.data.success) {
                alert('✅ کلمه ذخیره شد!');
            }
        } catch (error) {
            console.error(error);
            
            if (error.response?.data?.needSubscription) {
                if (window.confirm(
                    '⚠️ ذخیره کلمات فقط برای کاربران دارای اشتراک فعال امکان‌پذیر است.\n\n' +
                    'برای دسترسی به این قابلیت و فلش‌کارت، لطفاً اشتراک تهیه کنید.\n\n' +
                    'آیا می‌خواهید اشتراک تهیه کنید؟'
                )) {
                    window.location.href = '/subscription';
                }
            } else {
                alert(error.response?.data?.message || 'خطا در ذخیره کلمه');
            }
        }
    };

    const handleDictionaryWord = (word) => {
        const clean = String(word || '').trim();
        if (clean) {
            setDictionaryWord(clean);
            if (window.innerWidth <= 768) setDictionaryMobileOpen(true);
        }
    };

    if (loading) {
        return <div className="read-summary-loading">در حال بارگذاری کتاب…</div>;
    }

    return (
        <div className="read-summary-fullscreen">
            <div className="summary-content-area">
                {selectedNote ? (
                    <EReader
                        onBack={() => navigate('/summary-books')}
                        storageKey={bookId && selectedNote ? `${bookId}-${selectedNote._id}` : undefined}
                        content={content}
                        formatText={convertToEnglishNumbers}
                        bookTitle={bookTitle}
                        chapterTitle={`Chapter ${selectedNote.noteNumber}: ${selectedNote.title || `Note ${selectedNote.noteNumber}`}`}
                        audioSrc={selectedNote.audioLink || ''}
                        timingUrl={selectedNote.timingLink || selectedNote.transcriptUrl || ''}
                        emptyMessage={`محتوایی موجود نیست.${isAdmin ? " برای افزودن روی «ویرایش» کلیک کنید." : ''}`}
                        onWordSelect={handleDictionaryWord}
                        notes={notes}
                        selectedNote={selectedNote}
                        onNoteSelect={handleNoteClick}
                        isAdmin={isAdmin}
                        onAddNote={handleAddNote}
                        onDeleteNote={handleDeleteNote}
                        showNoteForm={showNoteForm}
                        setShowNoteForm={setShowNoteForm}
                        newNote={newNote}
                        setNewNote={setNewNote}
                        onEdit={handleEditClick}
                        editing={editMode}
                        editTitle={editTitle}
                        editContent={editContent}
                        editAudioLink={editAudioLink}
                        editTimingLink={editTimingLink}
                        setEditTimingLink={setEditTimingLink}
                        setEditTitle={setEditTitle}
                        setEditContent={setEditContent}
                        setEditAudioLink={setEditAudioLink}
                        onSaveEdit={handleSaveContent}
                        onCancelEdit={() => setEditMode(false)}
                        totalBookPages={countBookPages(notes)}
                        onEnded={goToNextNote}
                        onNextNote={goToNextNote}
                    />
                ) : (
                    <div className="no-selection"><p>در حال آماده‌سازی کتاب‌خوان…</p></div>
                )}
            </div>

            <DictionaryPanel
                selectedWord={dictionaryWord}
                apiBase={process.env.REACT_APP_API_URL}
                userId={currentUserId}
                bookId={bookId}
                bookTitle={bookTitle}
                onSaveWord={handleSaveWord}
                isMobileOpen={dictionaryMobileOpen}
                onToggleMobile={() => setDictionaryMobileOpen(v => !v)}
            />
        </div>
    );
}

export default ReadSummary;
